import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton } from 'components/modals';
import { EnterpriseScreenShell, actionButtonFactory, useCan } from 'components/enterprise-shell';
import { SyutGrid, SyutTreeGrid, type SyutGridColumn, type TreeGridColumn, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import { organisationService, currencyService } from 'api/services';
import type {
  OrganisationDto, OrgEntityDto, CoaTemplateDto, OrgLookupsDto, OrgBusinessUnitDto, OrgFinancialYearDto, OrgTaxRegistrationDto,
  SaveEntityRequest, SaveBusinessUnitRequest, SaveFinancialYearRequest, TaxFieldContextDto,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import TaxFieldsPanel, { validateTaxFields, type TaxFieldValues } from 'components/modules/tax/TaxFieldsPanel';

// ============================================================================
// ORGANISATION (Settings) — entities and their business units as one tree
// (entity → its units, a unit under the unit it reports to), with a ⋮ menu on
// every row. New, View and Edit of an entity share four tabs — Company, Tax and
// currency, Units and years, Chart of accounts — so each looks like the others;
// New walks them as steps (Back / Next, Create entity on the last).
//
// The first screen a fresh install's Super Administrator lands on: with no
// entity yet it offers "create your first entity" (entity + tax set-up + first
// business unit + first financial year in one step). The tax set-up is the tax
// engine's: a jurisdiction, then either the first registration (validated by
// the jurisdiction's own rules) or "not registered yet"; registrations are
// maintained afterwards in Tax Settings. After a change the user's access is
// reloaded, and the header is switched to the new entity, unit and year.
// ============================================================================

const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
const inputStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '0.375rem 0.625rem', width: '100%' };
const note: React.CSSProperties = { fontSize: '0.78rem', color: 'var(--text-secondary)' };
const smallBtn: React.CSSProperties = { fontSize: '0.8rem', padding: '0.3rem 0.75rem' };
const sectionHeading: React.CSSProperties = { margin: '0.25rem 0 0', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' };

const NUMBER_BINDING = 'REGISTRATION.NUMBER';
const STATE_BINDING = 'REGISTRATION.STATE';
const TYPE_BINDING = 'REGISTRATION.TYPE';

const fmtDate = (s?: string | null) => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** A year from the first day of this month, ending the day before the same date next year. */
const defaultYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(start.getFullYear() + 1, start.getMonth(), 0);
  const label = `${start.getFullYear()}${start.getMonth() === 0 ? '' : `-${String((start.getFullYear() + 1) % 100).padStart(2, '0')}`}`;
  return { code: `FY${label.replace('-', '')}`.slice(0, 20), name: `FY ${label}`, startDate: ymd(start), endDate: ymd(end) };
};

/** One row of the tree: an entity (root) or a business unit (under its entity, or the unit it reports to). */
interface OrgNode {
  id: string;
  parentId: string | null;
  kind: 'entity' | 'bu';
  code: string;
  name: string;
  typeName: string;
  country: string;
  jurisdiction: string;
  status: string | null;
  currentYear: string;
  entity: OrgEntityDto;
  bu?: OrgBusinessUnitDto;
}

/** New, View and Edit share one set of tabs, in this order (New walks them with Back / Next). */
type EntityTab = 'company' | 'tax' | 'units' | 'chart';
const ENTITY_TABS: EntityTab[] = ['company', 'tax', 'units', 'chart'];

const FRAMEWORK_LABEL: Record<string, string> = { IN_SCHEDULE_III: 'Schedule III', IN_NCE: 'ICAI non-corporate entities', BW_IFRS_SME: 'IFRS for SMEs' };
const humanizeCode = (c: string) => FRAMEWORK_LABEL[c] ?? c;

/** The last day of a 12-month year starting on `start` (yyyy-mm-dd): 2026-04-01 → 2027-03-31. */
const twelveMonthEnd = (start: string) => {
  const d = new Date(`${start.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : ymd(new Date(d.getFullYear() + 1, d.getMonth(), d.getDate() - 1));
};
const isTwelveMonths = (start: string, end: string) => !!start && twelveMonthEnd(start) === end.slice(0, 10);
/** Code and name for a year starting on `start`: FY2026 / FY 2026 for calendar years, else FY202627 / FY 2026-27. */
const yearLabel = (start: string) => {
  const d = new Date(`${start.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { code: '', name: '' };
  const calendar = d.getMonth() === 0 && d.getDate() === 1;
  const label = `${d.getFullYear()}${calendar ? '' : `-${String((d.getFullYear() + 1) % 100).padStart(2, '0')}`}`;
  return { code: `FY${label.replace('-', '')}`.slice(0, 20), name: `FY ${label}` };
};
/**
 * A changed start date: a regular year's end follows it (12 months), and the code and name follow
 * too while they are still the ones derived from the previous start.
 */
const onYearStart = (cur: { start: string; end: string; code: string; name: string; transition: boolean }, start: string) => {
  const prev = yearLabel(cur.start);
  const next = yearLabel(start);
  return {
    end: cur.transition ? cur.end : twelveMonthEnd(start),
    code: !cur.code || cur.code === prev.code ? next.code : cur.code,
    name: !cur.name || cur.name === prev.name ? next.name : cur.name,
  };
};

type Dialog =
  | { kind: 'entity-view'; entityId: string; tab: EntityTab }
  // fromView: opened from the entity view's Edit button, so closing returns there; from the tree it just closes.
  | { kind: 'entity-edit'; entity: OrgEntityDto; tab: EntityTab; fromView?: boolean }
  | { kind: 'entity-create'; tab: EntityTab }
  // fromView: opened from the entity view's Units and years tab, so closing returns there.
  | { kind: 'bu-view'; entity: OrgEntityDto; bu: OrgBusinessUnitDto; fromView?: boolean }
  | { kind: 'bu-edit'; entity: OrgEntityDto; editing: OrgBusinessUnitDto | null; fromView?: boolean }
  | null;

/** The year dialog stacks over the entity view, so it has its own state. */
type YearDialog = { entity: OrgEntityDto; editing: OrgFinancialYearDto | null } | null;

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode; hint?: string }> = ({ label, required, children, hint }) => (
  <div>
    <label style={labelStyle}>{label}{required && <span className="text-danger"> *</span>}</label>
    {children}
    {hint && <div style={{ ...note, fontSize: '0.72rem', marginTop: '0.2rem' }}>{hint}</div>}
  </div>
);

const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span className="badge" style={{ background: color, color: '#fff', fontSize: '0.7rem', marginRight: 4 }}>{children}</span>
);

const statusBadge = (status?: string | null) => status === 'REGISTERED'
  ? <Badge color="#059669">Registered</Badge>
  : status === 'NOT_REGISTERED' ? <Badge color="#64748b">Not registered</Badge> : <span style={note}>Not stated</span>;

/** A label / value list for the read-only views. */
const DetailList: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 230px) 1fr', columnGap: '1.5rem' }}>
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', padding: '0.45rem 0', borderBottom: '1px solid var(--border-color)' }}>{label}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', padding: '0.45rem 0', borderBottom: '1px solid var(--border-color)' }}>{value}</div>
      </React.Fragment>
    ))}
  </div>
);

/** Underlined modal tabs. */
const Tabs = <T extends string>({ tabs, active, onChange }: { tabs: { id: T; label: string; count?: number }[]; active: T; onChange: (t: T) => void }) => (
  <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)', marginBottom: '0.75rem' }}>
    {tabs.map(t => (
      <button key={t.id} type="button" onClick={() => onChange(t.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.9rem', fontSize: '0.85rem',
          color: active === t.id ? 'var(--primary)' : 'var(--text-primary)', fontWeight: active === t.id ? 600 : 500,
          borderBottom: `2px solid ${active === t.id ? 'var(--primary)' : 'transparent'}`, marginBottom: -1,
        }}>
        {t.label}{t.count !== undefined && <span style={{ ...note, marginLeft: 6 }}>{t.count}</span>}
      </button>
    ))}
  </div>
);

/** The house modal frame (header, scrolling body, footer). One is open at a time. */
const Modal: React.FC<{ title: string; onClose: () => void; busy?: boolean; footer: React.ReactNode; children: React.ReactNode; wide?: boolean }> =
  ({ title, onClose, busy, footer, children, wide }) => (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg"
        style={{ minHeight: 'auto', ...(wide ? { maxWidth: 1100, width: '95vw' } : {}) }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} disabled={busy}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content" style={{ display: 'grid', gap: '0.75rem', color: 'var(--text-primary)', alignContent: 'start' }}>{children}</div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>{footer}</div>
      </div>
    </>
  );

interface OrganisationScreenProps {
  moduleIcon?: string;
  tabId?: string;
}

const OrganisationScreen: React.FC<OrganisationScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('organisation');
  const { reloadPermissionContext, selectedEntity, selectedFinancialYear, setSelectedBranch, setSelectedFinancialYear } = useAuthContext();
  const [org, setOrg] = useState<OrganisationDto | null>(null);
  const [lookups, setLookups] = useState<OrgLookupsDto | null>(null);
  const [currencies, setCurrencies] = useState<{ code: string; name: string }[]>([]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [yearDialog, setYearDialog] = useState<YearDialog>(null);
  // Applying a chart template to an existing entity with an empty chart.
  const [chartDialog, setChartDialog] = useState<{ entity: OrgEntityDto; templateId: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields of the open edit dialog (one set, reset when a dialog opens); the year dialog has its own.
  const [f, setF] = useState<Record<string, string>>({});
  const [y, setY] = useState<Record<string, string>>({});
  const [withFirst, setWithFirst] = useState(true);
  const [taxValues, setTaxValues] = useState<TaxFieldValues>({});
  const [taxErrors, setTaxErrors] = useState<Record<string, string>>({});
  const upd = (patch: Record<string, string>) => setF(prev => ({ ...prev, ...patch }));
  const updY = (patch: Record<string, string>) => setY(prev => ({ ...prev, ...patch }));

  const load = useCallback(async () => {
    try {
      const o = await organisationService.get();
      setOrg(o);
      return o;
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load the organisation.');
      return null;
    }
  }, []);

  useEffect(() => {
    load();
    organisationService.getLookups().then(setLookups).catch(() => setLookups(null));
    currencyService.getAll()
      .then(list => setCurrencies(list.map(c => ({ code: c.currencyCode, name: c.currencyName }))))
      .catch(() => setCurrencies([]));
  }, [load]);

  const typeId = (code: string) => lookups?.businessUnitTypes.find(t => t.code === code)?.id ?? lookups?.businessUnitTypes[0]?.id ?? '';
  const entityById = (id: string) => org?.entities.find(e => e.id === id) ?? null;

  // The chosen jurisdiction's REGISTRATION field context (the tax engine's schema for the first registration).
  const taxContext: TaxFieldContextDto | null = useMemo(
    () => lookups?.jurisdictions.find(j => j.jurisdiction?.jurisdictionCode === f.jurisdictionCode) ?? null,
    [lookups, f.jurisdictionCode]);
  const parseNumber = useCallback((n: string) => organisationService.parseRegistration(taxContext!.jurisdiction!.id, n), [taxContext]);
  // A new entity's functional currency: the one picked, else the jurisdiction's — so an
  // untouched picker follows a change of jurisdiction.
  const createCurrency = f.currencyCode || taxContext?.jurisdiction?.currencyCode || '';

  // ---- open dialogs ----------------------------------------------------------
  const openCreateEntity = () => {
    const yr = defaultYear();
    setF({
      code: '', name: '', entityTypeId: '', registrationNumber: '', countryId: '',
      jurisdictionCode: lookups?.jurisdictions.length === 1 ? lookups.jurisdictions[0].jurisdiction?.jurisdictionCode ?? '' : '',
      registered: '1', regLabel: '', regFrom: '', currencyCode: '',
      buCode: 'HO', buName: 'Head Office', buTypeId: typeId('HEAD_OFFICE'), buCountryId: '',
      fyCode: yr.code, fyName: yr.name, fyStart: yr.startDate, fyEnd: yr.endDate,
    });
    setTaxValues({}); setTaxErrors({});
    setWithFirst(true);
    setDialog({ kind: 'entity-create', tab: 'company' });
  };
  const openEditEntity = (e: OrgEntityDto, tab: EntityTab = 'company', fromView = false) => {
    setF({
      code: e.code, name: e.name, entityTypeId: e.entityTypeId ?? '', registrationNumber: e.registrationNumber ?? '', countryId: e.countryId ?? '',
      currencyCode: e.functionalCurrencyCode ?? '', confirmCurrency: '',
    });
    setDialog({ kind: 'entity-edit', entity: e, tab, fromView });
  };
  const viewEntity = (entityId: string, tab: EntityTab = 'company') => setDialog({ kind: 'entity-view', entityId, tab });
  const openEditBu = (e: OrgEntityDto, editing: OrgBusinessUnitDto | null, parentUnitId?: string, fromView = false) => {
    setF(editing
      ? { code: editing.code, name: editing.name, unitTypeId: editing.unitTypeId, parentUnitId: editing.parentUnitId ?? '', countryId: editing.countryId ?? '' }
      : { code: '', name: '', unitTypeId: typeId('BRANCH'), parentUnitId: parentUnitId ?? '', countryId: e.countryId ?? e.businessUnits[0]?.countryId ?? '' });
    setDialog({ kind: 'bu-edit', entity: e, editing, fromView });
  };
  /** Closing a business-unit dialog opened from the entity view returns to its Units and years tab. */
  const closeBu = (e: OrgEntityDto, fromView?: boolean) => setDialog(fromView ? { kind: 'entity-view', entityId: e.id, tab: 'units' } : null);
  const openYear = (e: OrgEntityDto, editing: OrgFinancialYearDto | null) => {
    if (editing) {
      setY({ code: editing.code, name: editing.name, startDate: editing.startDate.slice(0, 10), endDate: editing.endDate.slice(0, 10), isCurrent: editing.isCurrent ? '1' : '',
        transition: isTwelveMonths(editing.startDate, editing.endDate) ? '' : '1' });
    } else {
      // The next year after the latest one, else a year from this month.
      const last = [...e.financialYears].sort((a, b) => b.endDate.localeCompare(a.endDate))[0];
      if (last) {
        const s = new Date(`${last.endDate.slice(0, 10)}T00:00:00`); s.setDate(s.getDate() + 1);
        const end = new Date(s.getFullYear() + 1, s.getMonth(), s.getDate() - 1);
        const label = `${s.getFullYear()}${s.getMonth() === 0 && s.getDate() === 1 ? '' : `-${String((s.getFullYear() + 1) % 100).padStart(2, '0')}`}`;
        setY({ code: `FY${label.replace('-', '')}`.slice(0, 20), name: `FY ${label}`, startDate: ymd(s), endDate: ymd(end), isCurrent: '', transition: '' });
      } else {
        const yr = defaultYear();
        setY({ code: yr.code, name: yr.name, startDate: yr.startDate, endDate: yr.endDate, isCurrent: '1', transition: '' });
      }
    }
    setYearDialog({ entity: e, editing });
  };

  /** The entity types of a country (plus any-country ones); every type when the country has none of its own. */
  const typesFor = (countryId: string) => {
    const all = lookups?.entityTypes ?? [];
    if (!countryId) return all.filter(t => !t.countryId);
    const own = all.filter(t => t.countryId === countryId);
    return own.length ? [...own, ...all.filter(t => !t.countryId)] : all;
  };

  /** Chart templates offered for a country: its own and the any-country ones. */
  const templatesFor = (countryId: string): CoaTemplateDto[] =>
    (lookups?.coaTemplates ?? []).filter(t => !t.countryId || t.countryId === countryId);
  /** The template a country and entity type start from: the one made for that legal form, if any. */
  const defaultTemplateFor = (countryId: string, entityTypeId: string): CoaTemplateDto | undefined => {
    const form = lookups?.entityTypes.find(t => t.id === entityTypeId)?.legalForm;
    return form ? templatesFor(countryId).find(t => (t.legalForms ?? []).includes(form)) : undefined;
  };
  /** The New Entity choice: '' = the default for the country and type, 'EMPTY' = no template, else a template id. */
  const chosenTemplate = (): CoaTemplateDto | undefined =>
    f.coaTemplateId === 'EMPTY' ? undefined
      : f.coaTemplateId ? lookups?.coaTemplates.find(t => t.id === f.coaTemplateId)
      : defaultTemplateFor(f.countryId, f.entityTypeId);

  /** The template choices: one card per template, plus "start empty" where offered. */
  const templateChoices = (countryId: string, entityTypeId: string, value: string, onPick: (v: string) => void, offerEmpty: boolean) => {
    const list = templatesFor(countryId);
    const dflt = defaultTemplateFor(countryId, entityTypeId);
    const selected = value || dflt?.id || (offerEmpty ? 'EMPTY' : '');
    const card = (id: string, title: React.ReactNode, sub: React.ReactNode) => (
      <label key={id} style={{
        display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.6rem 0.75rem', cursor: 'pointer',
        border: `1px solid ${selected === id ? 'var(--primary)' : 'var(--border-color)'}`, borderRadius: '0.5rem',
        background: selected === id ? 'var(--bg-secondary)' : 'transparent',
      }}>
        <input type="radio" checked={selected === id} onChange={() => onPick(id)} style={{ marginTop: 3 }} />
        <span style={{ minWidth: 0 }}>
          <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ display: 'block', ...note, marginTop: 2 }}>{sub}</span>
        </span>
      </label>
    );
    return (
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {list.length === 0 && <div style={note}>No chart template exists for this country yet.</div>}
        {list.map(t => card(t.id,
          <>{t.name}{dflt?.id === t.id && <span className="badge" style={{ marginLeft: 8, background: '#2563eb', color: '#fff', fontSize: '0.68rem' }}>Recommended</span>}</>,
          <>{t.ledgerCount} ledgers{t.reportingFramework ? ` · ${humanizeCode(t.reportingFramework)}` : ''}{t.description ? ` — ${t.description}` : ''}</>))}
        {offerEmpty && card('EMPTY', 'Start with an empty chart', 'Create groups and ledgers yourself, or apply a template later while the chart is still empty.')}
      </div>
    );
  };

  /** The entity's country narrows its types and, when creating, suggests the jurisdiction and the first unit's country. */
  const pickEntityCountry = (countryId: string) => {
    const patch: Record<string, string> = { countryId };
    if (f.entityTypeId && !typesFor(countryId).some(t => t.id === f.entityTypeId)) patch.entityTypeId = '';
    if (dialog?.kind === 'entity-create') {
      const code = lookups?.countries.find(c => c.id === countryId)?.code;
      const j = code ? lookups?.jurisdictions.find(x => (x.jurisdiction?.countryCode || '').toUpperCase() === code.toUpperCase()) : undefined;
      if (j?.jurisdiction && j.jurisdiction.jurisdictionCode !== f.jurisdictionCode) {
        patch.jurisdictionCode = j.jurisdiction.jurisdictionCode;
        setTaxValues({}); setTaxErrors({});
      }
      // The first unit is in the entity's country unless it was set to another one.
      if (!f.buCountryId || f.buCountryId === f.countryId) patch.buCountryId = countryId;
    }
    upd(patch);
  };

  /** Picking the first unit's country suggests that country's jurisdiction while none is chosen. */
  const pickCountry = (countryId: string) => {
    const code = lookups?.countries.find(c => c.id === countryId)?.code;
    const j = code ? lookups?.jurisdictions.find(x => (x.jurisdiction?.countryCode || '').toUpperCase() === code.toUpperCase()) : undefined;
    upd(!f.jurisdictionCode && j?.jurisdiction ? { buCountryId: countryId, jurisdictionCode: j.jurisdiction.jurisdictionCode } : { buCountryId: countryId });
  };

  /**
   * After a change: reload this screen and the user's access. When the header has no usable
   * entity (the first one was just created, or it still holds one from a database since re-created)
   * switch it to the new entity, its first unit and its current year; when the header is on this
   * entity but has no year yet, select its current year.
   */
  const afterChange = async (entityId?: string, switchHeader = false) => {
    const fresh = await load();
    try { await reloadPermissionContext(); } catch { /* the screen still shows the change */ }
    const e = entityId ? fresh?.entities.find(x => x.id === entityId) : undefined;
    const year = e ? (e.financialYears.find(x => x.isCurrent) ?? e.financialYears[0]) : undefined;
    const headerUsable = !!selectedEntity && !!fresh?.entities.some(x => x.id === selectedEntity.id);
    if (e && switchHeader && !headerUsable) {
      const bu = e.businessUnits[0];
      if (bu) setSelectedBranch(bu.id, bu.name, e.id, e.name);
      if (year) setSelectedFinancialYear(year.id, year.name, year.code);
      if (bu) notificationService.toast.success(`Working in ${e.name} · ${bu.name}${year ? ` · ${year.name}` : ''}. Next: the Setup checklist under Settings.`);
    } else if (e && year && selectedEntity?.id === e.id && !selectedFinancialYear) {
      setSelectedFinancialYear(year.id, year.name, year.code);
    }
    window.dispatchEvent(new Event('fbp:setup-changed'));
  };

  /** The first registration, shaped as Tax Settings shapes it: bound values from the jurisdiction's schema. */
  const buildRegistration = () => {
    const fields = taxContext?.fields || [];
    const bound = (binding: string) => {
      const fld = fields.find(x => (x.binding || '').toUpperCase() === binding);
      const v = fld ? taxValues[fld.fieldKey] : undefined;
      return v === undefined || v === null || v === '' ? null : String(v);
    };
    return {
      jurisdictionId: taxContext!.jurisdiction!.id,
      label: f.regLabel?.trim() || null,
      registrationNumber: (bound(NUMBER_BINDING) ?? '').trim().toUpperCase(),
      registrationType: bound(TYPE_BINDING),
      stateProvinceId: bound(STATE_BINDING),
      isDefault: true,
      effectiveFrom: f.regFrom || null,
      effectiveTo: null,
      attributes: taxValues,
      businessUnitIds: [],
      statusCode: 'ACTIVE',
    };
  };

  // ---- save ------------------------------------------------------------------
  const run = async (action: () => Promise<void>) => {
    setSaving(true);
    try {
      await action();
      notificationService.toast.success('Saved.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  /** New Entity: is this tab complete? Says what is missing when not. Next and Create both ask it. */
  const createTabOk = (t: EntityTab): boolean => {
    const fail = (msg: string) => { notificationService.toast.error(msg); return false; };
    switch (t) {
      case 'company':
        return f.countryId && f.code.trim() && f.name.trim() ? true : fail('Enter the country, code and legal name.');
      case 'tax': {
        if (!taxContext) return fail('Choose the tax jurisdiction.');
        if (!createCurrency) return fail('Choose the functional currency.');
        if (f.registered !== '1') { setTaxErrors({}); return true; }
        // Numbers and types are uppercase identities; mirror the server before the pattern check.
        const upper = { ...taxValues };
        (taxContext.fields || []).filter(x => x.binding === NUMBER_BINDING || x.binding === TYPE_BINDING)
          .forEach(x => { const v = upper[x.fieldKey]; if (typeof v === 'string') upper[x.fieldKey] = v.toUpperCase(); });
        const errs = validateTaxFields(taxContext, upper);
        setTaxErrors(errs);
        return Object.keys(errs).length === 0 ? true : fail('Complete the registration.');
      }
      case 'units':
        if (!withFirst) return true;
        if (!f.buCode?.trim() || !f.buName?.trim() || !f.buTypeId) return fail('Enter the first unit\'s code, name and type.');
        return f.fyCode?.trim() && f.fyName?.trim() && f.fyStart && f.fyEnd ? true : fail('Enter the first year\'s code, name and dates.');
      default:
        return true;
    }
  };

  const saveCreateEntity = async () => {
    const bad = ENTITY_TABS.find(t => !createTabOk(t));
    if (bad) { setDialog({ kind: 'entity-create', tab: bad }); return; }
    await run(async () => {
      const req: SaveEntityRequest = {
        code: f.code, name: f.name, entityTypeId: f.entityTypeId || null, countryId: f.countryId, registrationNumber: f.registrationNumber || null,
        tax: {
          jurisdictionCode: f.jurisdictionCode,
          // Shown and saved on the form, so the server stores it as confirmed (no Setup Checklist warning).
          functionalCurrencyCode: createCurrency,
          registered: f.registered === '1',
          effectiveFrom: f.regFrom || null,
          registration: f.registered === '1' ? buildRegistration() : null,
        },
      };
      req.coaTemplateId = chosenTemplate()?.id ?? null;
      if (withFirst) {
        req.firstBusinessUnit = { code: f.buCode, name: f.buName, unitTypeId: f.buTypeId, countryId: f.buCountryId || null };
        req.firstFinancialYear = { code: f.fyCode, name: f.fyName, startDate: f.fyStart, endDate: f.fyEnd, isCurrent: true, isTransitionPeriod: f.fyTransition === '1' };
      }
      const id = await organisationService.createEntity(req);
      setDialog(null);
      await afterChange(id, true);
    });
  };

  const saveEditEntity = (e: OrgEntityDto, tab: EntityTab, fromView?: boolean) => run(async () => {
    // The currency goes only when changed or ticked as confirmed, so a Company-only edit confirms nothing.
    const currencyChanged = !!f.currencyCode && f.currencyCode !== (e.functionalCurrencyCode ?? '');
    await organisationService.updateEntity(e.id, {
      code: f.code, name: f.name, entityTypeId: f.entityTypeId || null, countryId: f.countryId || null, registrationNumber: f.registrationNumber || null, rowVersion: e.rowVersion,
      functionalCurrencyCode: currencyChanged || f.confirmCurrency === '1' ? f.currencyCode : null,
    });
    if (fromView) viewEntity(e.id, tab); else setDialog(null);
    await afterChange(e.id);
  });

  const saveBu = (e: OrgEntityDto, editing: OrgBusinessUnitDto | null, fromView?: boolean) => run(async () => {
    const req: SaveBusinessUnitRequest = {
      code: f.code, name: f.name, unitTypeId: f.unitTypeId, parentUnitId: f.parentUnitId || null, countryId: f.countryId || null,
      rowVersion: editing?.rowVersion ?? null,
    };
    if (editing) await organisationService.updateBusinessUnit(editing.id, req);
    else await organisationService.createBusinessUnit(e.id, req);
    closeBu(e, fromView);
    await afterChange(e.id);
  });

  const saveYear = (e: OrgEntityDto, editing: OrgFinancialYearDto | null) => run(async () => {
    const req: SaveFinancialYearRequest = {
      code: y.code, name: y.name, startDate: y.startDate, endDate: y.endDate, isCurrent: y.isCurrent === '1', isTransitionPeriod: y.transition === '1',
      rowVersion: editing?.rowVersion ?? null,
    };
    if (editing) await organisationService.updateFinancialYear(editing.id, req);
    else await organisationService.createFinancialYear(e.id, req);
    setYearDialog(null);
    await afterChange(e.id);
  });

  const makeCurrent = async (e: OrgEntityDto, yr: OrgFinancialYearDto) => {
    try {
      await organisationService.updateFinancialYear(yr.id, {
        code: yr.code, name: yr.name, startDate: yr.startDate.slice(0, 10), endDate: yr.endDate.slice(0, 10), isCurrent: true, rowVersion: yr.rowVersion,
        isTransitionPeriod: !isTwelveMonths(yr.startDate, yr.endDate),
      });
      await afterChange(e.id);
      notificationService.toast.success(`${yr.name} is now the current year of ${e.name}.`);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not change the current year.');
    }
  };

  // ---- tree --------------------------------------------------------------------
  const nodes: OrgNode[] = useMemo(() => (org?.entities ?? []).flatMap(e => {
    const unitIds = new Set(e.businessUnits.map(b => b.id));
    return [
      {
        id: e.id, parentId: null, kind: 'entity' as const, code: e.code, name: e.name, typeName: e.entityTypeName || '',
        country: e.countryName || '', jurisdiction: e.jurisdictionName || '', status: e.registrationStatus ?? null,
        currentYear: e.financialYears.find(x => x.isCurrent)?.name || '', entity: e,
      },
      ...e.businessUnits.map(b => ({
        id: b.id, parentId: b.parentUnitId && unitIds.has(b.parentUnitId) ? b.parentUnitId : e.id, kind: 'bu' as const,
        code: b.code, name: b.name, typeName: b.unitTypeName || '', country: b.countryName || '',
        jurisdiction: '', status: null, currentYear: '', entity: e, bu: b,
      })),
    ];
  }), [org]);

  const openNode = (n: OrgNode) => setDialog(n.kind === 'entity'
    ? { kind: 'entity-view', entityId: n.entity.id, tab: 'company' }
    : { kind: 'bu-view', entity: n.entity, bu: n.bu! });

  const treeColumns: TreeGridColumn<OrgNode>[] = useMemo(() => [
    { id: 'name', header: 'Name', accessorKey: 'name', size: 360, isTreeColumn: true,
      cell: ({ row }) => (
        <button type="button" onClick={() => openNode(row.original)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: row.original.kind === 'entity' ? 600 : 400, textAlign: 'left' }}>
          {row.original.name}
        </button>
      ) },
    { id: 'code', header: 'Code', accessorKey: 'code', size: 120 },
    { id: 'type', header: 'Type', accessorKey: 'typeName', size: 160, cell: ({ row }) => row.original.typeName || '—' },
    { id: 'country', header: 'Country', accessorKey: 'country', size: 140, cell: ({ row }) => row.original.country || '—' },
    { id: 'jurisdiction', header: 'Tax jurisdiction', accessorKey: 'jurisdiction', size: 180,
      cell: ({ row }) => (row.original.kind === 'entity' ? row.original.jurisdiction || <span style={note}>Not set</span> : '') },
    { id: 'status', header: 'Tax status', size: 140, cell: ({ row }) => (row.original.kind === 'entity' ? statusBadge(row.original.status) : '') },
    { id: 'year', header: 'Current year', accessorKey: 'currentYear', size: 140,
      cell: ({ row }) => (row.original.kind === 'entity' ? row.original.currentYear || <span style={note}>None</span> : '') },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  const treeActions = (n: OrgNode): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [{ id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => openNode(n) }];
    if (n.kind === 'entity') {
      if (can('UPDATE')) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => openEditEntity(n.entity) });
      if (can('CREATE')) {
        items.push({ id: 'add-bu', label: 'Add business unit', icon: <IconSystem.add size={18} />, onClick: () => openEditBu(n.entity, null) });
        items.push({ id: 'add-fy', label: 'Add financial year', icon: <IconSystem.calendar size={18} />, onClick: () => openYear(n.entity, null) });
      }
      items.push({ id: 'years', label: 'Units and years', icon: <IconSystem.list size={18} />, onClick: () => viewEntity(n.entity.id, 'units') });
      if (can('UPDATE') && n.entity.groupCount === 0 && n.entity.glLedgerCount === 0) {
        items.push({ id: 'chart', label: 'Apply chart template', icon: <IconSystem.folder size={18} />,
          onClick: () => setChartDialog({ entity: n.entity, templateId: defaultTemplateFor(n.entity.countryId ?? '', n.entity.entityTypeId ?? '')?.id ?? '' }) });
      }
      items.push({ id: 'tax', label: 'Tax and currency', icon: <IconSystem.file size={18} />, onClick: () => viewEntity(n.entity.id, 'tax') });
    } else {
      if (can('UPDATE')) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => openEditBu(n.entity, n.bu!) });
      if (can('CREATE')) items.push({ id: 'add-sub', label: 'Add unit under it', icon: <IconSystem.add size={18} />, onClick: () => openEditBu(n.entity, null, n.bu!.id) });
    }
    return items;
  };

  // ---- grids inside the entity view ---------------------------------------------
  const fyColumns: SyutGridColumn<OrgFinancialYearDto>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 110 },
    { id: 'name', header: 'Name', accessorKey: 'name', size: 160 },
    { id: 'from', header: 'From', size: 120, cell: ({ row }) => fmtDate(row.original.startDate) },
    { id: 'to', header: 'To', size: 120, cell: ({ row }) => fmtDate(row.original.endDate) },
    { id: 'status', header: 'Status', size: 130,
      cell: ({ row }) => (
        <>
          {row.original.isCurrent && <Badge color="#059669">Current</Badge>}
          {row.original.isClosed && <Badge color="#64748b">Closed</Badge>}
          {!row.original.isCurrent && !row.original.isClosed && 'Open'}
        </>
      ) },
    { id: 'docs', header: 'Documents', size: 110, align: 'right', cell: ({ row }) => row.original.documentCount },
  ], []);

  const buColumns = (e: OrgEntityDto): SyutGridColumn<OrgBusinessUnitDto>[] => [
    { id: 'code', header: 'Code', accessorKey: 'code', size: 100 },
    { id: 'name', header: 'Name', accessorKey: 'name', size: 200 },
    { id: 'type', header: 'Type', accessorKey: 'unitTypeName', size: 140, cell: ({ row }) => row.original.unitTypeName || '—' },
    { id: 'parent', header: 'Reports to', size: 160, cell: ({ row }) => e.businessUnits.find(b => b.id === row.original.parentUnitId)?.name || '—' },
    { id: 'country', header: 'Country', accessorKey: 'countryName', size: 130, cell: ({ row }) => row.original.countryName || '—' },
  ];

  const regColumns: SyutGridColumn<OrgTaxRegistrationDto>[] = useMemo(() => [
    { id: 'number', header: 'Registration no.', accessorKey: 'registrationNumber', size: 170 },
    { id: 'label', header: 'Label', accessorKey: 'label', size: 140, cell: ({ row }) => row.original.label || '—' },
    { id: 'type', header: 'Type', accessorKey: 'registrationType', size: 110, cell: ({ row }) => row.original.registrationType || '—' },
    { id: 'state', header: 'State', accessorKey: 'stateName', size: 130, cell: ({ row }) => row.original.stateName || '—' },
    { id: 'units', header: 'Business units', accessorKey: 'businessUnitNames', size: 170, cell: ({ row }) => row.original.businessUnitNames || '—' },
    { id: 'from', header: 'From', size: 110, cell: ({ row }) => fmtDate(row.original.effectiveFrom) || '—' },
    { id: 'default', header: 'Default', size: 90, cell: ({ row }) => (row.original.isDefault ? <Badge color="#2563eb">Default</Badge> : '') },
  ], []);

  const canCreateEntity = !!org?.canCreateEntity && can('CREATE');
  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.custom('new-entity', <IconSystem.add size={18} />, 'New Entity', openCreateEntity), visible: canCreateEntity },
    actionButtonFactory.refresh(async () => { await load(); }),
  ];

  // ---- render pieces ---------------------------------------------------------------
  const cancelBtn = (onClick: () => void, label = 'Cancel') => <button className="btn btn-secondary" onClick={onClick} disabled={saving}>{label}</button>;
  const saveBtn = (onClick: () => void) => <button className="btn btn-primary" onClick={onClick} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>;

  const companyFields = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <Field label="Country" required hint="Where it is incorporated or registered. It decides the entity types offered and the default tax jurisdiction.">
          <select className="form-input" style={inputStyle} value={f.countryId} onChange={e => pickEntityCountry(e.target.value)}>
            <option value="">Choose…</option>{lookups?.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Type" hint={f.countryId ? undefined : 'Choose the country first.'}>
          <select className="form-input" style={inputStyle} value={f.entityTypeId} disabled={!f.countryId} onChange={e => upd({ entityTypeId: e.target.value })}>
            <option value="">—</option>{typesFor(f.countryId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '0.75rem' }}>
        <Field label="Code" required><input className="form-input" style={inputStyle} value={f.code} maxLength={50} onChange={e => upd({ code: e.target.value.toUpperCase() })} placeholder="e.g. ACME" /></Field>
        <Field label="Legal name" required><input className="form-input" style={inputStyle} value={f.name} maxLength={200} onChange={e => upd({ name: e.target.value })} placeholder="Legal name of the company" /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <Field label="Company registration no." hint="The company law registration (CIN in India, CIPA number in Botswana), not a tax number.">
          <input className="form-input" style={inputStyle} value={f.registrationNumber} maxLength={100} onChange={e => upd({ registrationNumber: e.target.value.toUpperCase() })} />
        </Field>
      </div>
    </>
  );

  /** The four tabs New, View and Edit share. New numbers them as steps and names tab 3 for what it creates. */
  const entityTabs = (tab: EntityTab, setTab: (t: EntityTab) => void, creating: boolean, e?: OrgEntityDto) => (
    <Tabs<EntityTab> active={tab} onChange={setTab} tabs={[
      { id: 'company', label: creating ? '1. Company' : 'Company' },
      { id: 'tax', label: creating ? '2. Tax and currency' : 'Tax and currency', count: e?.taxRegistrations.length },
      { id: 'units', label: creating ? '3. First unit and year' : 'Units and years', count: e ? e.businessUnits.length + e.financialYears.length : undefined },
      { id: 'chart', label: creating ? '4. Chart of accounts' : 'Chart of accounts' },
    ]} />
  );

  /** Every currency, plus the value itself when the list has not loaded or lacks it. */
  const currencySelect = (value: string, onChange: (v: string) => void, disabled = false) => (
    <select className="form-input" style={inputStyle} value={value} disabled={disabled} onChange={ev => onChange(ev.target.value)}>
      <option value="">Choose…</option>
      {value && !currencies.some(c => c.code === value) && <option value={value}>{value}</option>}
      {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
    </select>
  );

  const companyDetails = (e: OrgEntityDto) => (
    <DetailList rows={[
      ['Code', e.code],
      ['Legal name', e.name],
      ['Country', e.countryName || '—'],
      ['Type', e.entityTypeName || '—'],
      ['Company registration no.', e.registrationNumber || '—'],
    ]} />
  );

  const taxDetails = (e: OrgEntityDto) => (
    <DetailList rows={[
      ['Tax jurisdiction', e.jurisdictionName ? `${e.jurisdictionName} (${e.jurisdictionCode})` : <span style={note}>Not set: choose it in Tax Settings</span>],
      ['Functional currency', e.functionalCurrencyCode
        ? <>{e.functionalCurrencyCode}{!e.functionalCurrencyConfirmed && <span style={{ ...note, marginLeft: 8 }}><Badge color="#d97706">Not confirmed</Badge>set by default; confirm it with Edit</span>}{e.hasPostings && <span style={{ ...note, marginLeft: 8 }}>locked: the entity has postings</span>}</>
        : '—'],
      ['Tax registration status', statusBadge(e.registrationStatus)],
      ['Default tax registration', e.taxRegistrations.find(r => r.isDefault)?.registrationNumber || '—'],
    ]} />
  );

  /** The business units and financial years of an entity; with row actions on View, read-only on Edit. */
  const unitsAndYears = (e: OrgEntityDto, withActions: boolean) => (
    <>
      <h4 style={sectionHeading}>Business units</h4>
      <div style={{ minHeight: 160, display: 'flex', flexDirection: 'column' }}>
        <SyutGrid data={e.businessUnits} columns={buColumns(e)}
          rowActions={withActions ? bu => [
            { id: 'view', label: 'View', icon: <IconSystem.eye size={18} />, onClick: () => setDialog({ kind: 'bu-view', entity: e, bu, fromView: true }) },
            ...(can('UPDATE') ? [{ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => openEditBu(e, bu, undefined, true) }] : []),
            ...(can('CREATE') ? [{ id: 'add-sub', label: 'Add unit under it', icon: <IconSystem.add size={18} />, onClick: () => openEditBu(e, null, bu.id, true) }] : []),
          ] : undefined}
          emptyMessage="None yet: add the first business unit." />
      </div>
      <h4 style={sectionHeading}>Financial years</h4>
      <div style={{ minHeight: 160, display: 'flex', flexDirection: 'column' }}>
        <SyutGrid data={e.financialYears} columns={fyColumns}
          rowActions={withActions && can('UPDATE') ? yr => [
            { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => openYear(e, yr) },
            ...(!yr.isCurrent ? [{ id: 'current', label: 'Make current', icon: <IconSystem.check size={18} />, onClick: () => makeCurrent(e, yr) }] : []),
          ] : undefined}
          emptyMessage="None yet: add the first financial year." />
      </div>
    </>
  );

  const chartDetails = (e: OrgEntityDto) => (
    <DetailList rows={[
      ['Chart of accounts', e.groupCount || e.glLedgerCount
        ? `${e.glLedgerCount} ledgers${e.groupCount ? `, ${e.groupCount} groups` : ''}`
        : <span style={note}>Empty: apply a chart template while the chart has no groups or ledgers.</span>],
      ['Changed in', 'Accounts → Chart of Accounts'],
    ]} />
  );

  const renderDialog = () => {
    if (!dialog || !lookups) return null;
    switch (dialog.kind) {
      case 'entity-view': {
        const e = entityById(dialog.entityId);
        if (!e) return null;
        const tab = dialog.tab;
        const setTab = (t: EntityTab) => setDialog({ ...dialog, tab: t });
        const chartEmpty = e.groupCount === 0 && e.glLedgerCount === 0;
        return (
          <Modal title={`${e.name} (${e.code})`} wide onClose={() => setDialog(null)}
            footer={<>
              {(tab === 'company' || tab === 'tax') && can('UPDATE') && <button className="btn btn-secondary" onClick={() => openEditEntity(e, tab, true)}><IconSystem.edit size={14} /> Edit</button>}
              {tab === 'units' && can('CREATE') && <>
                <button className="btn btn-secondary" onClick={() => openEditBu(e, null, undefined, true)}><IconSystem.add size={14} /> Add business unit</button>
                <button className="btn btn-secondary" onClick={() => openYear(e, null)}><IconSystem.add size={14} /> Add financial year</button>
              </>}
              {tab === 'chart' && chartEmpty && can('UPDATE') && (
                <button className="btn btn-secondary" onClick={() => setChartDialog({ entity: e, templateId: defaultTemplateFor(e.countryId ?? '', e.entityTypeId ?? '')?.id ?? '' })}>
                  <IconSystem.folder size={14} /> Apply chart template
                </button>
              )}
              {cancelBtn(() => setDialog(null), 'Close')}
            </>}>
            {entityTabs(tab, setTab, false, e)}
            {tab === 'company' && companyDetails(e)}
            {tab === 'tax' && (
              <>
                {taxDetails(e)}
                <div style={note}>Registrations are maintained in <strong>Tax → Tax Settings</strong>, with this entity selected in the header.</div>
                <div style={{ minHeight: 180, display: 'flex', flexDirection: 'column' }}>
                  <SyutGrid data={e.taxRegistrations} columns={regColumns}
                    emptyMessage={e.registrationStatus === 'NOT_REGISTERED' ? 'Not registered: add the registration in Tax Settings once it is granted.' : 'No registrations.'} />
                </div>
              </>
            )}
            {tab === 'units' && unitsAndYears(e, true)}
            {tab === 'chart' && chartDetails(e)}
          </Modal>
        );
      }

      case 'entity-edit': {
        const e = dialog.entity;
        const { tab, fromView } = dialog;
        const setTab = (t: EntityTab) => setDialog({ ...dialog, tab: t });
        const back = () => (fromView ? viewEntity(e.id, tab) : setDialog(null));
        const unconfirmed = !e.functionalCurrencyConfirmed && f.currencyCode === (e.functionalCurrencyCode ?? '');
        return (
          <Modal title={`Edit entity — ${e.code}`} wide busy={saving} onClose={back}
            footer={<>{cancelBtn(back)}{saveBtn(() => saveEditEntity(e, tab, fromView))}</>}>
            {entityTabs(tab, setTab, false, e)}
            {tab === 'company' && companyFields}
            {tab === 'tax' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', alignItems: 'start' }}>
                  <Field label="Tax jurisdiction" hint="Changed in Tax → Tax Settings, with this entity selected in the header.">
                    <input className="form-input" style={inputStyle} disabled
                      value={e.jurisdictionName ? `${e.jurisdictionName} (${e.jurisdictionCode})` : 'Not set'} />
                  </Field>
                  <Field label="Functional currency" required
                    hint={e.hasPostings ? 'Locked: the entity already has postings.' : 'The currency the books are kept in. Locked once the entity has postings.'}>
                    {currencySelect(f.currencyCode, v => upd({ currencyCode: v, confirmCurrency: '' }), !!e.hasPostings)}
                  </Field>
                </div>
                {unconfirmed && (
                  <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8125rem' }}>
                    <input type="checkbox" checked={f.confirmCurrency === '1'} onChange={ev => upd({ confirmCurrency: ev.target.checked ? '1' : '' })} />
                    {e.functionalCurrencyCode} was set by default and is not confirmed yet. Confirm that the books are kept in {e.functionalCurrencyCode}.
                  </label>
                )}
                <div style={note}>Registrations and the registration status are maintained in Tax → Tax Settings.</div>
              </>
            )}
            {tab === 'units' && (
              <>
                <div style={note}>Business units and years have their own forms: add or change them from the entity's View → <strong>Units and years</strong>.</div>
                {unitsAndYears(e, false)}
              </>
            )}
            {tab === 'chart' && chartDetails(e)}
          </Modal>
        );
      }

      case 'entity-create': {
        const tab = dialog.tab;
        const setTab = (t: EntityTab) => setDialog({ kind: 'entity-create', tab: t });
        // Steps: Back / Next walk the tabs, Next only once the tab is complete; Create on the last.
        const step = ENTITY_TABS.indexOf(tab);
        const last = step === ENTITY_TABS.length - 1;
        return (
          <Modal title="New Entity" wide busy={saving} onClose={() => setDialog(null)}
            footer={<>
              {cancelBtn(() => setDialog(null))}
              {step > 0 && <button className="btn btn-secondary" onClick={() => setTab(ENTITY_TABS[step - 1])} disabled={saving}>Back</button>}
              {last
                ? <button className="btn btn-primary" onClick={saveCreateEntity} disabled={saving}>{saving ? 'Creating…' : 'Create entity'}</button>
                : <button className="btn btn-primary" onClick={() => { if (createTabOk(tab)) setTab(ENTITY_TABS[step + 1]); }} disabled={saving}>Next</button>}
            </>}>
            {entityTabs(tab, setTab, true)}
            {tab === 'company' && companyFields}
            {tab === 'tax' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '0.75rem', alignItems: 'end' }}>
                  <Field label="Tax jurisdiction" required hint="The tax regime the entity is under; can be changed later in Tax Settings.">
                    <select className="form-input" style={inputStyle} value={f.jurisdictionCode}
                      onChange={e => { upd({ jurisdictionCode: e.target.value }); setTaxValues({}); setTaxErrors({}); }}>
                      <option value="">Choose…</option>
                      {lookups.jurisdictions.filter(j => j.jurisdiction).map(j => (
                        <option key={j.jurisdiction!.id} value={j.jurisdiction!.jurisdictionCode}>{j.jurisdiction!.jurisdictionName} ({j.jurisdiction!.jurisdictionCode})</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Functional currency" required
                    hint={taxContext?.jurisdiction?.currencyCode && createCurrency !== taxContext.jurisdiction.currencyCode
                      ? `Not the jurisdiction's currency (${taxContext.jurisdiction.currencyCode}). Locked once the entity has postings.`
                      : 'The currency the books are kept in. Locked once the entity has postings.'}>
                    {currencySelect(createCurrency, v => upd({ currencyCode: v }))}
                  </Field>
                  <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.8125rem', paddingBottom: '1.4rem' }}>
                    <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', cursor: 'pointer' }}>
                      <input type="radio" checked={f.registered === '1'} onChange={() => upd({ registered: '1' })} /> Registered
                    </label>
                    <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', cursor: 'pointer' }}>
                      <input type="radio" checked={f.registered !== '1'} onChange={() => upd({ registered: '' })} /> Not registered yet
                    </label>
                  </div>
                </div>
                {taxContext && f.registered === '1' && (
                  <>
                    <TaxFieldsPanel context={taxContext} values={taxValues} bare disabled={saving} errors={taxErrors}
                      parseNumber={parseNumber}
                      onChange={(key, value) => setTaxValues(prev => ({ ...prev, [key]: value }))} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <Field label="Label" hint="Optional, e.g. the place of business.">
                        <input className="form-input" style={inputStyle} value={f.regLabel} maxLength={100} onChange={e => upd({ regLabel: e.target.value })} />
                      </Field>
                      <Field label="Registered from" hint={withFirst ? 'Defaults to the start of the first financial year.' : undefined} required={!withFirst}>
                        <input type="date" className="form-input" style={inputStyle} value={f.regFrom} onChange={e => upd({ regFrom: e.target.value })} />
                      </Field>
                    </div>
                    <div style={note}>It becomes the default registration{withFirst ? ', used by the first business unit' : ''}. More registrations are added in Tax Settings.</div>
                  </>
                )}
                {taxContext && f.registered !== '1' && (
                  <div style={note}>Documents charge no tax until a registration is added in Tax Settings.</div>
                )}
              </>
            )}
            {tab === 'chart' && (
              <>
                {!f.countryId
                  ? <div style={note}>Choose the country on the Company tab first; the templates offered are that country's.</div>
                  : <>
                      <div style={note}>
                        The working ledgers, ledger defaults and tax ledgers the entity starts with. Ledgers sit directly under their account category; no groups are created.
                        The recommended template matches the entity type.
                        Everything can be changed afterwards under Accounts.
                      </div>
                      {templateChoices(f.countryId, f.entityTypeId, f.coaTemplateId, v => upd({ coaTemplateId: v }), true)}
                    </>}
              </>
            )}
            {tab === 'units' && (
              <>
                <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={withFirst} onChange={e => setWithFirst(e.target.checked)} />
                  Also create its first business unit and financial year
                </label>
                {withFirst && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr', gap: '0.75rem' }}>
                      <Field label="Unit code" required><input className="form-input" style={inputStyle} value={f.buCode} maxLength={50} onChange={e => upd({ buCode: e.target.value.toUpperCase() })} /></Field>
                      <Field label="Unit name" required><input className="form-input" style={inputStyle} value={f.buName} maxLength={200} onChange={e => upd({ buName: e.target.value })} /></Field>
                      <Field label="Unit type" required>
                        <select className="form-input" style={inputStyle} value={f.buTypeId} onChange={e => upd({ buTypeId: e.target.value })}>
                          {lookups.businessUnitTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </Field>
                      <Field label="Country">
                        <select className="form-input" style={inputStyle} value={f.buCountryId} onChange={e => pickCountry(e.target.value)}>
                          <option value="">—</option>{lookups.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </Field>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr 1fr', gap: '0.75rem' }}>
                      <Field label="Year code" required><input className="form-input" style={inputStyle} value={f.fyCode} maxLength={20} onChange={e => upd({ fyCode: e.target.value.toUpperCase() })} /></Field>
                      <Field label="Year name" required><input className="form-input" style={inputStyle} value={f.fyName} maxLength={100} onChange={e => upd({ fyName: e.target.value })} /></Field>
                      <Field label="From" required>
                        <input type="date" className="form-input" style={inputStyle} value={f.fyStart}
                          onChange={e => {
                            const n = onYearStart({ start: f.fyStart, end: f.fyEnd, code: f.fyCode, name: f.fyName, transition: f.fyTransition === '1' }, e.target.value);
                            upd({ fyStart: e.target.value, fyEnd: n.end, fyCode: n.code, fyName: n.name });
                          }} />
                      </Field>
                      <Field label="To" required hint={f.fyTransition === '1' ? 'A month end, at most 18 months.' : '12 months from the start.'}>
                        <input type="date" className="form-input" style={inputStyle} value={f.fyEnd} disabled={f.fyTransition !== '1'} onChange={e => upd({ fyEnd: e.target.value })} />
                      </Field>
                    </div>
                    <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8125rem' }}>
                      <input type="checkbox" checked={f.fyTransition === '1'}
                        onChange={e => upd(e.target.checked ? { fyTransition: '1' } : { fyTransition: '', fyEnd: twelveMonthEnd(f.fyStart) })} />
                      First year from incorporation, or a change of year-end (shorter or longer than 12 months)
                    </label>
                    <div style={note}>The year becomes the entity's current year and the default in the header.</div>
                  </>
                )}
              </>
            )}
          </Modal>
        );
      }

      case 'bu-view': {
        const { entity: e, bu, fromView } = dialog;
        return (
          <Modal title={`${bu.name} (${bu.code})`} onClose={() => closeBu(e, fromView)}
            footer={<>
              {can('UPDATE') && <button className="btn btn-secondary" onClick={() => openEditBu(e, bu, undefined, fromView)}><IconSystem.edit size={14} /> Edit</button>}
              {cancelBtn(() => closeBu(e, fromView), 'Close')}
            </>}>
            <DetailList rows={[
              ['Entity', `${e.name} (${e.code})`],
              ['Code', bu.code],
              ['Name', bu.name],
              ['Type', bu.unitTypeName || '—'],
              ['Reports to', e.businessUnits.find(b => b.id === bu.parentUnitId)?.name || '—'],
              ['Country', bu.countryName || '—'],
              ['Issues under tax registration', e.taxRegistrations.find(r => (r.businessUnitNames || '').split(', ').includes(bu.name))?.registrationNumber
                || (e.taxRegistrations.find(r => r.isDefault)?.registrationNumber ? `${e.taxRegistrations.find(r => r.isDefault)!.registrationNumber} (entity default)` : '—')],
            ]} />
          </Modal>
        );
      }

      case 'bu-edit': {
        const { entity: e, editing, fromView } = dialog;
        return (
          <Modal title={`${editing ? `Edit business unit — ${editing.code}` : 'New business unit'} · ${e.name}`} busy={saving} onClose={() => closeBu(e, fromView)}
            footer={<>{cancelBtn(() => closeBu(e, fromView))}{saveBtn(() => saveBu(e, editing, fromView))}</>}>
            <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.75rem' }}>
              <Field label="Code" required><input className="form-input" style={inputStyle} value={f.code} maxLength={50} onChange={ev => upd({ code: ev.target.value.toUpperCase() })} /></Field>
              <Field label="Name" required><input className="form-input" style={inputStyle} value={f.name} maxLength={200} onChange={ev => upd({ name: ev.target.value })} /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
              <Field label="Type" required>
                <select className="form-input" style={inputStyle} value={f.unitTypeId} onChange={ev => upd({ unitTypeId: ev.target.value })}>
                  {lookups.businessUnitTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
              <Field label="Reports to">
                <select className="form-input" style={inputStyle} value={f.parentUnitId} onChange={ev => upd({ parentUnitId: ev.target.value })}>
                  <option value="">— (directly under the entity)</option>
                  {e.businessUnits.filter(b => b.id !== editing?.id).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Country">
                <select className="form-input" style={inputStyle} value={f.countryId} onChange={ev => upd({ countryId: ev.target.value })}>
                  <option value="">—</option>{lookups.countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
            </div>
          </Modal>
        );
      }
    }
    return null;
  };

  const renderChartDialog = () => {
    if (!chartDialog) return null;
    const { entity: e, templateId } = chartDialog;
    const apply = () => run(async () => {
      if (!templateId) throw Object.assign(new Error(), { response: { data: { message: 'Choose a template.' } } });
      const res = await organisationService.applyCoaTemplate(e.id, templateId);
      setChartDialog(null);
      await afterChange(e.id);
      notificationService.toast.success(`Chart created: ${res.ledgers} ledgers, ${res.ledgerDefaults} ledger defaults, ${res.taxAccounts} tax ledgers.${res.notes.length ? ` ${res.notes.join(' ')}` : ''}`);
    });
    return (
      <Modal title={`Apply chart template · ${e.name}`} busy={saving} onClose={() => setChartDialog(null)}
        footer={<>{cancelBtn(() => setChartDialog(null))}<button className="btn btn-primary" onClick={apply} disabled={saving || !templateId}>{saving ? 'Applying…' : 'Apply'}</button></>}>
        <div style={note}>The entity's chart is empty. The template creates its working ledgers directly under their account categories (no groups), sets the ledger defaults and maps the tax ledgers of its jurisdiction.</div>
        {templateChoices(e.countryId ?? '', e.entityTypeId ?? '', templateId, v => setChartDialog({ entity: e, templateId: v }), false)}
      </Modal>
    );
  };

  const renderYearDialog = () => {
    if (!yearDialog) return null;
    const { entity: e, editing } = yearDialog;
    const fixed = !!editing && editing.documentCount > 0;
    return (
      <Modal title={`${editing ? `Edit financial year — ${editing.code}` : 'New financial year'} · ${e.name}`} busy={saving} onClose={() => setYearDialog(null)}
        footer={<>{cancelBtn(() => setYearDialog(null))}{saveBtn(() => saveYear(e, editing))}</>}>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr', gap: '0.75rem' }}>
          <Field label="Code" required><input className="form-input" style={inputStyle} value={y.code} maxLength={20} onChange={ev => updY({ code: ev.target.value.toUpperCase() })} /></Field>
          <Field label="Name" required><input className="form-input" style={inputStyle} value={y.name} maxLength={100} onChange={ev => updY({ name: ev.target.value })} /></Field>
          <Field label="From" required hint={fixed ? 'Fixed: documents are dated in this year' : undefined}>
            <input type="date" className="form-input" style={inputStyle} value={y.startDate} disabled={fixed}
              onChange={ev => {
                const n = onYearStart({ start: y.startDate, end: y.endDate, code: y.code, name: y.name, transition: y.transition === '1' }, ev.target.value);
                updY({ startDate: ev.target.value, endDate: n.end, code: n.code, name: n.name });
              }} />
          </Field>
          <Field label="To" required hint={fixed ? undefined : y.transition === '1' ? 'A month end, at most 18 months.' : '12 months from the start.'}>
            <input type="date" className="form-input" style={inputStyle} value={y.endDate} disabled={fixed || y.transition !== '1'} onChange={ev => updY({ endDate: ev.target.value })} />
          </Field>
        </div>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8125rem' }}>
          <input type="checkbox" checked={y.transition === '1'} disabled={fixed}
            onChange={ev => updY(ev.target.checked ? { transition: '1' } : { transition: '', endDate: twelveMonthEnd(y.startDate) })} />
          First year from incorporation, or a change of year-end (shorter or longer than 12 months)
        </label>
        <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.8125rem' }}>
          <input type="checkbox" checked={y.isCurrent === '1'} onChange={ev => updY({ isCurrent: ev.target.checked ? '1' : '' })} />
          The current year (the default in the header)
        </label>
      </Modal>
    );
  };

  // ---- render ----------------------------------------------------------------------
  const empty = org && org.entities.length === 0;

  return (
    <EnterpriseScreenShell
      title="Organisation"
      description="Entities and their business units, financial years and tax registrations"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      {empty && (
        <div style={{ maxWidth: 560, margin: '3rem auto', textAlign: 'center', border: '1px solid var(--border-color)', borderRadius: '0.75rem', padding: '2rem', color: 'var(--text-primary)' }}>
          <IconSystem.folder size={40} />
          <h3 style={{ margin: '0.75rem 0 0.4rem' }}>Create your first entity</h3>
          <p style={{ ...note, fontSize: '0.85rem' }}>
            Nothing is set up yet. Start with the company (entity), its tax jurisdiction and registration, its head office or first
            branch, and its first financial year. Everything else (accounting settings, voucher types, users) follows from there.
          </p>
          {canCreateEntity
            ? <button className="btn btn-primary" style={smallBtn} onClick={openCreateEntity} disabled={!lookups}><IconSystem.add size={14} /> Create the first entity</button>
            : <p style={note}>Only a user with access to the whole company can add entities.</p>}
        </div>
      )}

      {org && !empty && (
        <div className="data-grid-container" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <SyutTreeGrid data={nodes} columns={treeColumns} parentKey="parentId"
            showConnectorLines showNodeIcons defaultExpandLevel={-1}
            rowActions={treeActions} emptyMessage="No entities." />
        </div>
      )}

      {/* One dialog at a time: a year or chart-template form opened from the entity view
          replaces it while open, and the view (same tab) returns when it closes. */}
      {!(dialog?.kind === 'entity-view' && (yearDialog || chartDialog)) && renderDialog()}
      {renderYearDialog()}
      {renderChartDialog()}
    </EnterpriseScreenShell>
  );
};

export default OrganisationScreen;
